import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

// Fikset: Lagt til FiInfo i importen
const { 
  FiPackage, FiCheck, FiPlus, FiX, FiAlertTriangle, 
  FiFileText, FiCreditCard, FiLayers, FiShoppingCart, FiLoader, FiSave, FiRefreshCw, FiInfo 
} = FiIcons;

const SubscriptionPanel = ({ companyId: propCompanyId }) => {
  const { user } = useAuth();
  const { 
    companies, 
    subscriptionPlans, 
    externalServices, 
    updateCompany, 
    loading 
  } = useData();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Finn bedriften (enten fra props eller fra innlogget bruker)
  const company = useMemo(() => {
    const targetId = propCompanyId || user?.companyId || user?.company_id;
    if (!companies || !targetId) return null;
    return companies.find(c => c.id === targetId);
  }, [companies, user, propCompanyId]);

  // Tilstand for "kladde-endringer"
  const [draft, setDraft] = useState(null);

  // Synkroniser draft med company når company endres (første gang eller etter lagring)
  useEffect(() => {
    if (company && !draft) {
      setDraft({
        subscription: company.subscription || 'standard',
        external_services: company.external_services || [],
        departments_count: company.departments_count || 1
      });
    }
  }, [company]);

  // --- HJELPEFUNKSJONER ---
  
  const getIncludedDepartments = (planId) => {
    if (planId === 'konsern' || planId === 'offentlig-kommunal') return 1; // Konsern prises fra første enhet
    if (planId === 'standard') return 5;
    return 1;
  };

  const calculateTotal = (config) => {
    if (!config) return 0;
    const plan = subscriptionPlans.find(p => p.id === config.subscription) || subscriptionPlans[0];
    let total = 0;
    const deptCount = config.departments_count || 1;

    if (plan?.multi_unit) {
      // Konsern/Offentlig: Pris per enhet
      total = Number(plan.price || 0) * deptCount;
    } else {
      // Standard/Grunn: Fast pris + ekstra avdelinger
      total = Number(plan?.price || 0);
      const included = getIncludedDepartments(config.subscription);
      const extra = Math.max(0, deptCount - included);
      total += (extra * 990);
    }

    if (Array.isArray(config.external_services)) {
      config.external_services.forEach(serviceId => {
        const service = externalServices.find(s => s.id === serviceId);
        // Kun inkluder tjenester med 'mnd' (månedlig) i månedsprisen
        if (service && (service.timeframe === 'mnd' || !service.timeframe)) {
          total += Number(service.price || 0);
        }
      });
    }
    return total;
  };

  const hasChanges = useMemo(() => {
    if (!company || !draft) return false;
    return (
      draft.subscription !== company.subscription ||
      JSON.stringify([...draft.external_services].sort()) !== JSON.stringify([...(company.external_services || [])].sort()) ||
      draft.departments_count !== company.departments_count
    );
  }, [company, draft]);

  // --- HANDLINGER ---

  const handleToggleServiceDraft = (serviceId) => {
    setDraft(prev => {
      const services = [...prev.external_services];
      if (services.includes(serviceId)) {
        return { ...prev, external_services: services.filter(id => id !== serviceId) };
      } else {
        return { ...prev, external_services: [...services, serviceId] };
      }
    });
  };

  const handleAddDeptDraft = () => {
    setDraft(prev => ({ ...prev, departments_count: (prev.departments_count || 1) + 1 }));
  };

  const handleSetPlanDraft = (planId) => {
    setDraft(prev => ({ ...prev, subscription: planId }));
    setShowChangePlanModal(false);
  };

  const handleSaveChanges = async () => {
    if (!company || !draft) return;
    
    setIsProcessing(true);
    try {
      const newTotal = calculateTotal(draft);
      const updates = {
        subscription: draft.subscription,
        external_services: draft.external_services,
        departments_count: draft.departments_count,
        monthly_fee: newTotal,
        updated_at: new Date().toISOString()
      };

      const res = await updateCompany(company.id, updates);
      if (res.success) {
        setSaveSuccess(true);
        // Oppdater draft til å matche det som ble lagret for å fjerne "har endringer" statusen
        setDraft({
          subscription: updates.subscription,
          external_services: updates.external_services,
          departments_count: updates.departments_count
        });
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error(res.error);
      }
    } catch (error) {
      alert("Feil ved lagring: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetDraft = () => {
    setDraft({
      subscription: company.subscription,
      external_services: company.external_services || [],
      departments_count: company.departments_count || 1
    });
  };

  if (loading || !draft) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-500">
        <SafeIcon icon={FiLoader} className="text-4xl animate-spin mb-4 text-blue-500" />
        <p>Henter abonnementsdata...</p>
      </div>
    );
  }

  const currentPlan = subscriptionPlans.find(p => p.id === draft.subscription) || subscriptionPlans[0];
  const totalPrice = calculateTotal(draft);

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      
      {/* Sticky Action Bar for Pending Changes */}
      <AnimatePresence>
        {hasChanges && (
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[90] w-[90%] max-w-2xl bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-2xl shadow-2xl p-4 flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/40 rounded-lg text-blue-600">
                <SafeIcon icon={FiInfo} />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-white">Ulagrede endringer</p>
                <p className="text-xs text-gray-500">Ny pris: <span className="font-bold text-blue-600">{totalPrice},- / mnd</span></p>
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={handleResetDraft}
                className="px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
              >
                Angre
              </button>
              <button 
                onClick={handleSaveChanges}
                disabled={isProcessing}
                className="px-6 py-2 bg-green-600 text-white rounded-xl font-black text-sm hover:bg-green-700 shadow-lg flex items-center gap-2 disabled:opacity-50"
              >
                {isProcessing ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
                Lagre og Aktiver
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            Abonnement & Faktura
            {saveSuccess && (
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full flex items-center gap-1 font-black uppercase">
                <SafeIcon icon={FiCheck} /> Lagret!
              </motion.span>
            )}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">Administrer bedriftens pakker og tilleggstjenester.</p>
        </div>
        <div className="bg-white dark:bg-gray-800 px-6 py-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold">Månedspris (eks. mva)</p>
          <p className={`text-3xl font-black transition-colors ${hasChanges ? 'text-orange-500' : 'text-blue-600 dark:text-blue-400'}`}>
            {totalPrice.toLocaleString('no-NO')} kr 
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          
          {/* Main Plan Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white dark:bg-gray-700 rounded-xl shadow-sm text-blue-600 dark:text-blue-400">
                  <SafeIcon icon={FiPackage} size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{currentPlan?.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">{currentPlan?.employees}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowChangePlanModal(true)}
                className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-bold text-blue-600 dark:text-blue-400 hover:bg-blue-50 transition-all shadow-sm"
              >
                Endre pakke
              </button>
            </div>
            
            <div className="p-6 grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-wider">Inkludert i {currentPlan?.name}</h4>
                <ul className="space-y-2">
                  {currentPlan?.features?.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                      <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-2xl border border-gray-100 dark:border-gray-600">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <SafeIcon icon={FiLayers} className="text-indigo-500" /> Avdelinger
                    </h4>
                    <span className="text-xs font-black bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded">
                      {draft.departments_count} enheter
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mb-4 uppercase font-bold">
                    {currentPlan?.multi_unit ? `${currentPlan.price},- per enhet / mnd` : `${getIncludedDepartments(draft.subscription)} inkludert • ${Math.max(0, draft.departments_count - getIncludedDepartments(draft.subscription))} ekstra (+990,-/stk)`}
                  </p>
                  <button 
                    onClick={handleAddDeptDraft}
                    className="w-full py-3 bg-white dark:bg-gray-800 border-2 border-indigo-100 dark:border-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-xl text-sm font-black hover:bg-indigo-50 transition-all flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiPlus} /> Legg til ekstra avdeling
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Add-on Services List */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Tilleggstjenester</h3>
              <p className="text-sm text-gray-500">Aktiver eller deaktiver tjenester for din bedrift.</p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {externalServices.filter(s => s.active).map(service => {
                const isActiveInDraft = draft.external_services.includes(service.id);
                
                return (
                  <div key={service.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-6 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${isActiveInDraft ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        <SafeIcon icon={FiCheck} size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white">{service.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{service.description}</p>
                        <p className="text-sm font-black text-blue-600 mt-2">{service.price},- / {service.timeframe}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleToggleServiceDraft(service.id)} 
                      className={`min-w-[140px] py-2.5 rounded-xl text-xs font-black transition-all border-2 flex items-center justify-center gap-2 ${
                        isActiveInDraft 
                        ? 'border-red-100 text-red-600 hover:bg-red-50' 
                        : 'bg-blue-600 border-transparent text-white hover:bg-blue-700 shadow-lg'
                      }`}
                    >
                      {isActiveInDraft ? (hasChanges ? 'Fjern fra kladd' : 'Deaktiver') : 'Legg til i kladd'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-md font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <SafeIcon icon={FiShoppingCart} className="text-blue-500" /> Ditt abonnement
            </h3>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  {currentPlan?.name} 
                  {currentPlan?.multi_unit ? ` (${draft.departments_count} enheter)` : ''}
                </span>
                <span className="font-bold">
                  {currentPlan?.multi_unit 
                    ? (currentPlan.price * draft.departments_count).toLocaleString('no-NO') 
                    : currentPlan.price.toLocaleString('no-NO')},-
                </span>
              </div>
              {!currentPlan?.multi_unit && draft.departments_count > getIncludedDepartments(draft.subscription) && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Ekstra avdelinger ({draft.departments_count - getIncludedDepartments(draft.subscription)} stk)</span>
                  <span className="font-bold">{(Math.max(0, draft.departments_count - getIncludedDepartments(draft.subscription)) * 990).toLocaleString('no-NO')},-</span>
                </div>
              )}
              {draft.external_services.map(id => {
                const s = externalServices.find(x => x.id === id);
                if (s?.timeframe === 'mnd') {
                  return (
                    <div key={id} className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{s.name}</span>
                      <span className="font-bold">{s.price.toLocaleString('no-NO')},-</span>
                    </div>
                  );
                }
                return null;
              })}
              <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
                <span className="font-black text-gray-900 dark:text-white uppercase text-xs">Månedspris totalt</span>
                <span className="text-xl font-black text-blue-600 dark:text-blue-400">{totalPrice.toLocaleString('no-NO')} kr</span>
              </div>
            </div>

            <h3 className="text-md font-bold text-gray-900 dark:text-white mb-6 pt-6 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2">
              <SafeIcon icon={FiCreditCard} className="text-blue-500" /> Betalingsinfo
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Betalingsmåte</span>
                <span className="font-bold capitalize">{company.paymentMethod || 'Faktura'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Felt i SuperAdmin</span>
                <span className="font-bold text-blue-600">Oppdatert automatisk</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Plan Modal */}
      <AnimatePresence>
        {showChangePlanModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-2xl w-full p-8">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-black">Velg ny pakke</h3>
                <button onClick={() => setShowChangePlanModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><SafeIcon icon={FiX} size={24} /></button>
              </div>
              <div className="grid gap-4">
                {subscriptionPlans.filter(p => p.active).map(plan => (
                  <div 
                    key={plan.id} 
                    onClick={() => handleSetPlanDraft(plan.id)}
                    className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${draft.subscription === plan.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-100 dark:border-gray-700 hover:border-blue-200'}`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-bold text-lg">{plan.name}</h4>
                        <p className="text-sm text-gray-500">{plan.employees}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-black text-blue-600">{plan.price},-</p>
                        {draft.subscription === plan.id && <span className="text-[10px] font-black uppercase text-blue-500">Valgt</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionPanel;